import { getBackendUrl } from '../../lib/api';
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Loader2, FileText, MessageSquare,
  Brain, PiggyBank, Calendar,
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../lib/api';
import CandidateFinancialsCard from '../../components/admin/CandidateFinancialsCard';
import ProfileHeader from '../../components/admin/candidate/ProfileHeader';
import QualificationTab from '../../components/admin/candidate/QualificationTab';
import NotesTab from '../../components/admin/candidate/NotesTab';
import CVAnalysisTab from '../../components/admin/candidate/CVAnalysisTab';
import CandidateFicheTab from '../../components/admin/candidate/CandidateFicheTab';
import CandidatePipelineStatus from '../../components/admin/candidate/CandidatePipelineStatus';
import CandidateAIBrief from '../../components/admin/candidate/CandidateAIBrief';
import CandidateOwnership from '../../components/admin/candidate/CandidateOwnership';
import CandidateMessagesPanel from '../../components/admin/candidate/CandidateMessagesPanel';
import MeetingsPanel from '../../components/admin/candidate/MeetingsPanel';
import OnboardingCard from '../../components/admin/OnboardingCard';
import CandidateDocumentsTab from '../../components/admin/candidate/CandidateDocumentsTab';
import SalaryBenchmarkCard from '../../components/admin/candidate/SalaryBenchmarkCard';
import CandidateEditDialogs from '../../components/admin/candidate/CandidateEditDialogs';
import {
  getExperienceBadge,
  determineQualificationFormType,
} from '../../components/admin/candidate/candidateHelpers';
import { useAuth } from '../../contexts/AuthContext';

const CandidateProfilePage = () => {
  const { candidateId } = useParams();
  const { user: currentUser } = useAuth();
  const [candidate, setCandidate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newNote, setNewNote] = useState('');
  const [updating, setUpdating] = useState(false);
  const [categories, setCategories] = useState([]);
  const [qualification, setQualification] = useState(null);
  
  // Edit states
  const [editingSection, setEditingSection] = useState(null);
  const [editData, setEditData] = useState({});
  const [savingProfile, setSavingProfile] = useState(false);
  const [sendingEval, setSendingEval] = useState(false);
  const [meetings, setMeetings] = useState([]);

  useEffect(() => {
    fetchCandidate();
    fetchCategories();
    fetchQualification();
    fetchMeetings();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidateId]);

  const fetchMeetings = async () => {
    try {
      const res = await api.get(`/admin/meetings/candidate/${candidateId}`);
      if (res.ok) {
        const data = await res.json();
        setMeetings(data.meetings || []);
      }
    } catch (_) { /* silent */ }
  };

  // Send evaluation form to candidate via email (1-click)
  const sendEvaluationForm = async () => {
    setSendingEval(true);
    try {
      const response = await api.post(`/admin/qualification/send-evaluation/${candidateId}`);
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message || 'Formulaire envoyé');
        fetchCandidate(); // Refresh to show evaluation_sent status
      } else {
        const err = await response.json();
        toast.error(err.detail || "Erreur lors de l'envoi");
      }
    } catch (error) {
      toast.error("Erreur lors de l'envoi du formulaire");
    } finally {
      setSendingEval(false);
    }
  };

  const fetchQualification = async () => {
    try {
      const response = await api.get(`/admin/candidates/${candidateId}/qualification`);
      if (response.ok) {
        const data = await response.json();
        if (data.has_qualification) {
          setQualification(data);
        }
      }
    } catch (error) {
      console.error('Failed to fetch qualification:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch(getBackendUrl() + '/api/job-categories');
      const data = await response.json();
      setCategories(data.categories || []);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const fetchCandidate = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/admin/candidates/${candidateId}`);
      const data = await response.json();
      setCandidate(data);
    } catch (error) {
      console.error('Failed to fetch candidate:', error);
      toast.error('Erreur lors du chargement du candidat');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (newStatus) => {
    setUpdating(true);
    try {
      const response = await api.patch(`/admin/candidates/${candidateId}/status`, {
        status: newStatus
      });
      if (response.ok) {
        // Parse response to detect auto-onboarding trigger (backend returns
        // `onboarding: {ok, reason, portal_url, ...}` when status→talent_pool)
        const data = await response.json().catch(() => ({}));
        const onboarding = data?.onboarding;

        if (newStatus === 'talent_pool' && onboarding?.ok) {
          // Fresh auto-onboarding email sent
          toast.success('Statut mis à jour · email d\'onboarding envoyé au candidat', {
            duration: 6000,
            description: onboarding.reused ? 'Session existante réutilisée' : 'Nouvelle session créée',
          });
        } else if (newStatus === 'talent_pool' && onboarding && !onboarding.ok) {
          // Auto-onboarding was attempted but skipped (already sent, completed, no email, etc.)
          const reasonMap = {
            already_sent: 'email d\'onboarding déjà envoyé précédemment',
            already_completed: 'onboarding déjà terminé par le candidat',
            no_candidate_email: 'pas d\'email candidat renseigné',
          };
          const reasonLabel = reasonMap[onboarding.reason] || onboarding.reason || 'non envoyé';
          toast.info(`Statut mis à jour · onboarding non déclenché (${reasonLabel})`, {
            duration: 6000,
          });
        } else {
          toast.success('Statut mis à jour');
        }
        fetchCandidate();
      } else if (response.status === 403) {
        const err = await response.json().catch(() => ({}));
        toast.error(err.detail || "Vous n'êtes pas autorisé à modifier ce candidat (candidat assigné à un autre admin).", { duration: 7000 });
      } else {
        toast.error('Erreur lors de la mise à jour');
      }
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setUpdating(false);
    }
  };

  const addNote = async () => {
    if (!newNote.trim()) return;
    
    try {
      const response = await api.post(`/admin/candidates/${candidateId}/notes`, {
        note: newNote,
        admin_name: 'Admin'
      });
      if (response.ok) {
        toast.success('Note ajoutée');
        setNewNote('');
        fetchCandidate();
      }
    } catch (error) {
      toast.error('Erreur lors de l\'ajout de la note');
    }
  };

  const downloadCV = async () => {
    try {
      const token = sessionStorage.getItem('token');
      const response = await fetch(
        `${getBackendUrl()}/api/admin/candidates/${candidateId}/cv`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      if (!response.ok) {
        throw new Error('Erreur lors du téléchargement');
      }
      
      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'cv.pdf';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename=(.+)/);
        if (filenameMatch) {
          filename = filenameMatch[1].replace(/"/g, '');
        }
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('CV téléchargé avec succès');
    } catch (error) {
      console.error('Download CV error:', error);
      toast.error('Erreur lors du téléchargement du CV');
    }
  };

  // =====================
  // EDIT PROFILE FUNCTIONS
  // =====================
  
  const startEditing = (section) => {
    const analysis = candidate.ai_analysis || {};
    
    switch (section) {
      case 'category':
        // Use new structure: primary_category
        setEditData({ 
          primary_category: candidate.primary_category || getPrimaryCategory(candidate),
          job_categories: candidate.job_categories || []
        });
        break;
      case 'summary':
        setEditData({ summary: analysis.summary || '' });
        break;
      case 'education':
        setEditData({ education: analysis.education || [{ degree: '', institution: '', year: '' }] });
        break;
      case 'experience':
        setEditData({ work_experience: analysis.work_experience || [{ title: '', company: '', duration: '', description: '' }] });
        break;
      case 'skills':
        setEditData({ skills: analysis.skills?.join(', ') || '' });
        break;
      case 'tools':
        setEditData({ tools: analysis.tools?.join(', ') || '' });
        break;
      case 'languages':
        setEditData({ languages: analysis.languages || [{ language: '', level: '' }] });
        break;
      case 'strengths':
        setEditData({ strengths: analysis.strengths?.join('\n') || '' });
        break;
      default:
        break;
    }
    setEditingSection(section);
  };

  const saveProfileSection = async () => {
    setSavingProfile(true);
    try {
      let payload = {};
      
      switch (editingSection) {
        case 'category':
          // Update with new structure
          payload = { 
            primary_category: editData.primary_category,
            job_categories: [{
              category_id: editData.primary_category,
              is_primary: true,
              added_at: new Date().toISOString(),
              source: "admin_edited"
            }]
          };
          break;
        case 'summary':
          payload = { summary: editData.summary };
          break;
        case 'education':
          payload = { education: editData.education.filter(e => e.degree) };
          break;
        case 'experience':
          payload = { work_experience: editData.work_experience.filter(e => e.title) };
          break;
        case 'skills':
          payload = { skills: editData.skills.split(',').map(s => s.trim()).filter(s => s) };
          break;
        case 'tools':
          payload = { tools: editData.tools.split(',').map(s => s.trim()).filter(s => s) };
          break;
        case 'languages':
          payload = { languages: editData.languages.filter(l => l.language) };
          break;
        case 'strengths':
          payload = { strengths: editData.strengths.split('\n').map(s => s.trim()).filter(s => s) };
          break;
        default:
          break;
      }
      
      const response = await api.patch(`/admin/candidates/${candidateId}/profile`, payload);
      
      if (response.ok) {
        toast.success('Profil mis à jour');
        setEditingSection(null);
        fetchCandidate();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de la mise à jour');
      }
    } catch (error) {
      console.error('Save profile failed:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSavingProfile(false);
    }
  };

  const addListItem = (field) => {
    const items = editData[field] || [];
    let newItem = {};
    
    if (field === 'education') {
      newItem = { degree: '', institution: '', year: '' };
    } else if (field === 'work_experience') {
      newItem = { title: '', company: '', duration: '', description: '' };
    } else if (field === 'languages') {
      newItem = { language: '', level: '' };
    }
    
    setEditData({ ...editData, [field]: [...items, newItem] });
  };

  const removeListItem = (field, index) => {
    const items = editData[field] || [];
    setEditData({ ...editData, [field]: items.filter((_, i) => i !== index) });
  };

  const updateListItem = (field, index, key, value) => {
    const items = [...(editData[field] || [])];
    items[index] = { ...items[index], [key]: value };
    setEditData({ ...editData, [field]: items });
  };

  const getExperienceBadge = (years) => {
    if (years <= 2) return { label: 'Junior', class: 'bg-level-junior text-white' };
    if (years <= 5) return { label: 'Intermédiaire', class: 'bg-level-confirme text-white' };
    return { label: 'Senior', class: 'bg-level-senior text-white' };
  };

  const getCategoryLabel = (categoryId) => {
    const cat = categories.find(c => c.id === categoryId);
    return cat ? cat.name : categoryId || 'Non spécifié';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-8 w-8 animate-spin text-tp-text-light" />
      </div>
    );
  }

  if (!candidate) {
    return (
      <div className="p-4 md:p-8">
        <p className="text-tp-text-light">Candidat non trouvé</p>
      </div>
    );
  }

  const analysis = candidate.ai_analysis;
  const expBadge = getExperienceBadge(candidate.years_of_experience);

  return (
    <div className="p-4 md:p-8" data-testid="candidate-profile-page">
      <ProfileHeader
        candidate={candidate}
        candidateId={candidateId}
        expBadge={expBadge}
        qualification={qualification}
        sendEvaluationForm={sendEvaluationForm}
        sendingEval={sendingEval}
        downloadCV={downloadCV}
        fetchCandidate={fetchCandidate}
      />

      {/* Pipeline stepper + État du candidat — accessible en haut de page */}
      <div className="mb-4 md:mb-6 space-y-3 md:space-y-4">
        {/* Analyse IA — bloc principal de lecture rapide, EN PREMIER */}
        <CandidateAIBrief
          qualification={qualification}
          candidate={candidate}
          sendEvaluationForm={sendEvaluationForm}
          sendingEval={sendingEval}
        />
        <CandidateOwnership
          candidate={candidate}
          currentUser={currentUser}
          onUpdated={fetchCandidate}
        />
        <CandidatePipelineStatus
          candidate={candidate}
          updating={updating}
          updateStatus={updateStatus}
        />
        <OnboardingCard candidate={candidate} />
      </div>

      <Tabs defaultValue="fiche" className="space-y-4 md:space-y-6">
        <div className="overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0">
          <TabsList className="bg-tp-bg inline-flex w-auto overflow-x-auto max-w-full scrollbar-none">
            <TabsTrigger value="fiche" className="text-xs sm:text-sm whitespace-nowrap px-2 sm:px-3">Fiche</TabsTrigger>
            <TabsTrigger value="qualification" className="flex items-center gap-1 text-xs sm:text-sm whitespace-nowrap px-2 sm:px-3">
              <Brain className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              <span className="hidden sm:inline">Qualification</span>
              <span className="sm:hidden">Qualif.</span>
            </TabsTrigger>
            <TabsTrigger value="cv" className="text-xs sm:text-sm whitespace-nowrap px-2 sm:px-3">CV</TabsTrigger>
            <TabsTrigger value="messages" className="flex items-center gap-1 text-xs sm:text-sm whitespace-nowrap px-2 sm:px-3">
              <MessageSquare className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              <span>Messages</span>
            </TabsTrigger>
            <TabsTrigger value="documents" className="flex items-center gap-1 text-xs sm:text-sm whitespace-nowrap px-2 sm:px-3">
              <FileText className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              <span>Documents</span>
            </TabsTrigger>
            <TabsTrigger value="entretiens" className="flex items-center gap-1 text-xs sm:text-sm whitespace-nowrap px-2 sm:px-3">
              <Calendar className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              <span>Entretiens</span>
            </TabsTrigger>
            <TabsTrigger value="rentabilite" className="flex items-center gap-1 text-xs sm:text-sm whitespace-nowrap px-2 sm:px-3">
              <PiggyBank className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              <span className="hidden sm:inline">Rentabilité</span>
              <span className="sm:hidden">Renta.</span>
            </TabsTrigger>
            <TabsTrigger value="notes" className="text-xs sm:text-sm whitespace-nowrap px-2 sm:px-3">Notes</TabsTrigger>
          </TabsList>
        </div>

        {/* FICHE CANDIDAT - Structured Canvas */}
        <TabsContent value="fiche" className="space-y-4 md:space-y-6">
          <CandidateFicheTab
            candidate={candidate}
            analysis={analysis}
            updating={updating}
            updateStatus={updateStatus}
            startEditing={startEditing}
            candidateId={candidateId}
            onCategoriesUpdated={fetchCandidate}
            meetings={meetings}
          />

          {/* Salary Information with Benchmark */}
          <SalaryBenchmarkCard candidate={candidate} candidateId={candidateId} onUpdated={fetchCandidate} />
        </TabsContent>

        {/* QUALIFICATION Tab - Analyse IA détaillée */}
        <TabsContent value="qualification" className="space-y-4 md:space-y-6">
          <QualificationTab
            candidate={candidate}
            candidateId={candidateId}
            qualification={qualification}
            updateStatus={updateStatus}
            determineFormType={determineQualificationFormType}
          />
        </TabsContent>

        {/* CV & ANALYSE Tab */}
        <TabsContent value="cv" className="space-y-4 md:space-y-6">
          <CVAnalysisTab candidate={candidate} analysis={analysis} downloadCV={downloadCV} />
        </TabsContent>

        {/* MESSAGES Tab - per-candidate inbox */}
        <TabsContent value="messages" className="space-y-4 md:space-y-6">
          <CandidateMessagesPanel
            candidateId={candidateId}
            candidateEmail={candidate.email}
            candidateName={`${candidate.first_name || ''} ${candidate.last_name || ''}`.trim()}
          />
        </TabsContent>

        {/* DOCUMENTS Tab - upload CI, certificats, lettres de reco + RGPD audit */}
        <TabsContent value="documents" className="space-y-4 md:space-y-6">
          <CandidateDocumentsTab
            candidateId={candidateId}
            candidate={candidate}
            canModify={(() => {
              const assigned = (candidate?.assigned_to || '').toLowerCase();
              const me = (currentUser?.email || '').toLowerCase();
              const isSuper = ['super_admin','superadmin','owner','root'].includes((currentUser?.role || '').toLowerCase());
              return !assigned || isSuper || assigned === me;
            })()}
          />
        </TabsContent>

        {/* ENTRETIENS Tab — Meetings minimaliste avec liens manuels + décision post-call */}
        <TabsContent value="entretiens" className="space-y-4 md:space-y-6">
          <MeetingsPanel
            candidateId={candidateId}
            candidate={candidate}
            onCandidateRefresh={fetchCandidate}
          />
        </TabsContent>


        {/* Rentabilité Tab - SUPER_ADMIN only */}
        <TabsContent value="rentabilite" className="space-y-4 md:space-y-6">
          {/* Salary Benchmark Card */}
          <SalaryBenchmarkCard candidate={candidate} candidateId={candidateId} onUpdated={fetchCandidate} />
          
          {/* Financial Details */}
          <CandidateFinancialsCard 
            candidateId={candidateId} 
            candidateName={`${candidate.first_name} ${candidate.last_name}`}
          />
        </TabsContent>

        {/* Notes Tab */}
        <TabsContent value="notes" className="space-y-4 md:space-y-6">
          <NotesTab 
            candidate={candidate}
            newNote={newNote}
            setNewNote={setNewNote}
            addNote={addNote}
          />
        </TabsContent>
      </Tabs>

      <CandidateEditDialogs
        editingSection={editingSection}
        setEditingSection={setEditingSection}
        editData={editData}
        setEditData={setEditData}
        savingProfile={savingProfile}
        saveProfileSection={saveProfileSection}
        addListItem={addListItem}
        removeListItem={removeListItem}
        updateListItem={updateListItem}
      />
    </div>
  );
};

export default CandidateProfilePage;
